import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../commonComponents/translate';
import Modal from '../../commonComponents/modal/Modal';
import ModalHeader from '../../commonComponents/modal/ModalHeader';
import CreditCardSelector from '../profile/mainPanel/forms/payment/creditCardSelector/CreditCardSelector';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import withCSRF from '../../commonComponents/withCSRF';
import {
  checkStripeKeys,
  compose,
  displayMoney, handleStripePayment,
  IN_APP_PRODUCT_TYPE,
} from '../../services/utilityFunctions';
import { useCards, useLoader } from '../../services/customHooks';
import StripeService from '../../services/stripeService';
import Notifications from '../../services/notifications';
import Request, { hideLoader, showLoader } from '../../services/request';
import Loader from '../../services/loader';
import GoogleAnalytics, { ENROLL_TYPE } from '../../services/googleAnalytics';
import DiscountInput from '../shared/DiscountInput';
import i18n from '../../services/i18n';

const configForItemType = (itemType, id, slug) => {
  switch (itemType) {
    case IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE:
      return {
        title: 'purchase_modal_title',
        fetchUrl: `/certificate/${id}/data`,
        fetchError: 'error_fetchCertificate',
        enrollParams: ({ price, currency }) => ({ amount: price, currency }),
        enrollUrl: `/certificate/${id}/email`,
        enrollError: 'error_certificatePurchase',
        enrollSuccess: 'certificate_purchased',
      };
    case ENROLL_TYPE.JOURNEY:
      return {
        title: 'enroll_modal_title',
        fetchUrl: `/journey/${id}/json`,
        fetchError: 'error_fetchJourney',
        enrollParams: ({ price, currency }) => ({
          journey: id,
          price,
          currency,
        }),
        enrollUrl: '/journey/purchase',
        enrollError: 'error_journeyEnroll',
        enrollSuccess: 'academy_enrollSuccess',
        enrollRedirect: `/journey/${slug || id}`,
        fetchPrices: `/api/journey/${id}/purchase/calculate?`,
      };
    case ENROLL_TYPE.COURSE:
    default:
      return {
        title: 'enroll_modal_title',
        fetchUrl: `/academy/${id}/data`,
        fetchError: 'error_fetchCourse',
        enrollParams: ({ owner, price, currency }) => ({
          owner,
          course: id,
          price,
          currency,
        }),
        enrollUrl: '/academy/enroll',
        enrollError: 'error_courseEnroll',
        enrollSuccess: 'academy_enrollSuccess',
        enrollRedirect: `/academy/${slug || id}`,
        fetchPrices: `/api/course/${id}/purchase/calculate?`,
      };
  }
};

const getURL = (url, user) => {
  const { shippingCountry, currency = 'eur', personalInfo = {}, address } = user;
  let auxURL = url;
  if (currency) {
    auxURL += `currency=${currency}`;
  }
  let country;
  // Check if individual
  if (!personalInfo?.businessType || personalInfo?.businessType === 'individual') {
    country = shippingCountry || address?.countryCode;
  } else {
    country = address?.countryCode || shippingCountry;
  }
  if (country) {
    auxURL += `&country=${country}`;
  }
  return auxURL;
};

function EnrollModal(props) {
  const {
    t,
    _csrf,
    itemType,
    itemId,
    itemSlug,
    cards: propCards,
    onClose,
    type,
  } = props;
  const {
    title,
    fetchUrl,
    fetchError,
    enrollUrl,
    enrollParams,
    enrollError,
    enrollSuccess,
    enrollRedirect,
    fetchPrices,
  } = configForItemType(itemType, itemId, itemSlug);
  const [pricesState, setPricesState] = useState({});
  const { loggedUser } = useContext(LoggedUserContext);

  const loader = useRef(new Loader('#modalBody'));
  const { data, isLoading } = useLoader(fetchUrl, fetchError, {});
  useEffect(() => (isLoading ? loader.current.show() : loader.current.hide()), [
    isLoading,
  ]);

  const { cards, handleAddCard, handleRemoveCard, handleSelectCard } = useCards(
    _csrf,
    'credit-card-container',
    propCards,
  );

  const [cardError, setCardError] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(null);

  const hasOldPrice =
    data.inAppProduct &&
    data.inAppProduct.userCurrency &&
    data.inAppProduct.userCurrency.total;
  const oldCurrency = hasOldPrice ? data.inAppProduct.userCurrency : null;

  const hasPromotion =
    data.inAppPromotion &&
    data.inAppPromotion.userCurrency &&
    data.inAppPromotion.userCurrency.total;
  const promotionCurrency = hasPromotion
    ? data.inAppPromotion.userCurrency
    : null;

  const userCurrency = promotionCurrency || oldCurrency || {};
  const subTotal = Math.max(userCurrency.price - discountAmount, 0);
  const taxAmount = subTotal * (userCurrency.tax / 100);
  const priceItems = [
    { label: t('print_modal_price'), value: userCurrency.price },
    { label: t('print_modal_tax'), value: taxAmount },
    { label: t('print_modal_total'), value: subTotal + taxAmount },
  ];
  if (discountAmount !== null) {
    priceItems.splice(1, 0, {
      label: t('print_modal_discount'),
      value: discountAmount * -1,
    });
  }

  useEffect(
    () => {
      if (!coupon) {
        setDiscountAmount(null);
      } else {
        new Request()
          .get(
            `/discount/${coupon.code}/calculate?product=${
              data.inAppProduct.product
            }&price=${userCurrency.price}`,
          )
          .done(({ data: amount }) => {
            setDiscountAmount(amount);
          })
          .fail(err => {
            setDiscountAmount(null);
            Notifications.showErrorFromRequest(err);
          });
      }
    },
    [coupon],
  );

  const performPayment = async cardId =>
    StripeService.initiatePayment(_csrf, {
      product: data.inAppProduct.product,
      quantity: 1,
      currency: userCurrency.code,
      amount: userCurrency.price,
      paymentMethod: cardId,
      customerId: loggedUser.stripe.customerId,
      coupon: coupon ? coupon._id : undefined,
    });

  const performEnroll = ({ amount, currency }) => {
    const params = enrollParams({
      owner: loggedUser._id,
      price: amount,
      currency,
    });

    new Request(_csrf)
      .post(enrollUrl, params)
      .done(({ data: purchase }) => {
        if (itemType === ENROLL_TYPE.COURSE) {
          GoogleAnalytics.courseBought(data, amount, purchase._id);
        } else if (itemType === ENROLL_TYPE.JOURNEY) {
          GoogleAnalytics.journeyBought(data, amount, purchase._id);
        }

        Notifications.showNotificationSuccess(t('success'), t(enrollSuccess));
        if (enrollRedirect) {
          window.location.href = enrollRedirect;
        } else {
          onClose();
        }
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, enrollError);
      });
  };

  const loadingPurchase = loading => {
    if (loading) {
      showLoader();
    } else {
      hideLoader();
    }
  };

  // Handle the payment
  const confirmPayment = async paymentIntentId => new Promise((resolve, reject) => {
    const url = type === 'courses' ? `course/${itemId}/confirm_payment` : `journey/${itemId}/confirm_payment`;

    try {
      axios.post(`/api/${url}`, {
        paymentIntentId,
      }, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
        .then(({ data: response }) => {
          resolve(response.data);
          Notifications.showNotificationSuccess(t('success'), t('academy_enrollSuccess'));
        })
        .catch(err => reject(err));
    } catch (err) {
      reject(err);
    }
  });

  const createPaymentRequest = async card => new Promise((resolve, reject) => {
    const url = type === 'courses' ? `/api/course/${itemId}/capture_payment?` : `/api/journey/${itemId}/capture_payment?`;
    const finalUrl = getURL(url, loggedUser);
    try {
      let params = {
        paymentMethodId: card.id,
      };
      if (coupon && coupon._id) {
        params = { ...params, coupon: coupon._id };
      }
      axios.post(finalUrl, params,
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'License',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        }).then(async ({ data: response }) => {
        if (response.data.status === 'requires_confirmation') {
          try {
            await handleStripePayment(
              card.id,
              response.data.client_secret,
            );
          } catch (err) {
            Notifications.showErrorFromRequest(
              err,
              'ERROR_PAYMENT_DENIED',
            );
            reject(err);
          }
          try {
            const paymentIntent = await confirmPayment(response.data.id);
            resolve(paymentIntent);
          } catch (err) {
            Notifications.showErrorFromRequest(
              err,
              type === 'courses' ? 'error_courseEnroll' : 'error_journeyEnroll',
            );
            reject(err);
          }
        } else {
          resolve(response.data);
        }
      }).catch(err => {
        Notifications.showErrorFromRequest(
          err,
          type === 'courses' ? 'error_courseEnroll' : 'error_journeyEnroll',
        );
        reject(err);
      });
    } catch (err) {
      reject(err);
    }
  });

  const createPayment = async card => {
    try {
      loadingPurchase(true);
      await createPaymentRequest(card);
      window.location.reload();
    } catch (err) {
      console.log(err);
      loadingPurchase(false);
    }
  };
  const handleSubmit = async () => {
    try {
      const card = cards.find(c => c.isDefault);

      if (!card) {
        setCardError(true);
        return null;
      }

      if (checkStripeKeys(loggedUser)) {
        Notifications.showNotificationError(
          t('error'),
          t('ERROR_MISSING_CUSTOMER'),
        );
        return null;
      }

      if (!userCurrency.price || !userCurrency.code) {
        Notifications.showNotificationError(
          t('error'),
          t('error_missingUserCurrency'),
        );
        return null;
      }

      if (type === 'generic') {
        let result;
        if (subTotal !== 0) {
          result = await performPayment(card.id);
        } else {
          result = { amount: 0, currency: userCurrency.code };
        }
        performEnroll(result);
      } else {
       await createPayment(card);
      }

      return null;
    } catch (err) {
      Notifications.showNotificationError(t('error'), t(err));
      return null;
    }
  };

  const item = data || {};
  const certificateName =
    itemType === IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE
      ? t('certificate_title')
      : null;
  const name = item.name || item.title || certificateName || '';

  const getPrices = () => {
    const finalURL = getURL(fetchPrices, loggedUser);

    const params = {};
    if (coupon && coupon._id) {
      params.coupon = coupon._id;
    }
    axios.post(finalURL, params,
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        const { price: priceData, total, discount, subtotalTax } = response.data;
        const priceItemsToShow = [
          { label: t('print_modal_price'), value: priceData },
          { label: t('print_modal_tax'), value: subtotalTax },
          { label: t('print_modal_total'), value: total },
        ];
        if (discount > 0) {
          priceItemsToShow.splice(1, 0, {
            label: t('print_modal_discount'),
            value: discount * -1,
          });
        }
        setPricesState(priceItemsToShow);
      })
      .catch(err => {
        console.log('*Err', err);
        const errorKey = itemType === ENROLL_TYPE.JOURNEY ? i18n.__n('journey_title') : i18n.__n('journey_stepType_course');
        Notifications.showErrorFromRequest(err,
          i18n.__n('ERROR_CALCULATE_PRICES_ACADEMY', errorKey),
        );
      });
  };

  useEffect(() => {
    if (itemType === ENROLL_TYPE.JOURNEY || itemType === ENROLL_TYPE.COURSE) {
      getPrices();
    }
  }, [coupon]);

  const renderPrices = itemPrices => (
      itemPrices.map(i => (
        <div className="price-item" key={i.label}>
          <span className="price-label">{i.label}</span>
          <span className="price-value">
            {displayMoney(i.value, userCurrency.symbol)}
          </span>
        </div>
      ))
    );
  return (
    <Modal onClose={onClose} tabIndex={null}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader title={t(title)} onClose={onClose} />
          <div
            id="modalBody"
            className="modal-body"
            style={{ maxHeight: '70vh', overflowY: 'auto' }}
          >
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="print-details-content">
                    <div>
                      <h4>{`${t(title)}: ${name}`}</h4>
                      {(hasOldPrice || pricesState.length > 0) ? (
                        <div className="price-items-container">
                          {renderPrices(pricesState.length > 0 ? pricesState : priceItems)}
                        </div>
                      ) : (
                        <p className="price">{t('print_modal_noPrice')}</p>
                      )}
                    </div>
                    <br />
                    {hasOldPrice && <DiscountInput onChange={setCoupon} />}
                    <br />
                    <div id="credit-card-container">
                      <CreditCardSelector
                        cards={cards}
                        onSelectCard={handleSelectCard}
                        hasError={cardError}
                        isModeSelection
                        onAddCard={handleAddCard}
                        onRemoveCard={handleRemoveCard}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              {t('cancel')}
            </button>
            <button
              disabled={isLoading}
              type="submit"
              className="btn btn-dynamic loader-button"
              onClick={handleSubmit}
            >
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

EnrollModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  itemSlug: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)),
  itemType: PropTypes.oneOf(
    Object.values(ENROLL_TYPE).concat(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE),
  ),
  type: PropTypes.string, // Type that selects the endpoint to use
};

EnrollModal.defaultProps = {
  cards: [],
  itemType: ENROLL_TYPE.COURSE,
  itemSlug: null,
  type: 'generic',
};

export default compose(
  translate,
  withCSRF,
)(EnrollModal);
